import { FC, memo, useCallback, useEffect, useState } from 'react'
import { $generateHtmlFromNodes } from '@lexical/html'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import {
  mention as mentionConst,
  entityReference as entityReferenceConst,
} from '@crew/configs/constants'
import { EntityType } from '@crew/enums/domain'
import _ from 'lodash'
import { COMMAND_PRIORITY_EDITOR, BLUR_COMMAND } from 'lexical'
import { getAttributesFromHtml, getAttributeSetsFromHtml } from 'utils/html'
import { RelatedEntity } from '@crew/models/common'

export const ValueChangeEvent = {
  /**
   * 入力が確定(入力後フォーカスアウト)したらイベントが発火する
   */
  change: 'change',
  /**
   * 文字が入力されたらイベントが発火する
   */
  input: 'input',
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ValueChangeEvent =
  (typeof ValueChangeEvent)[keyof typeof ValueChangeEvent]

type Props = {
  valueChangeEvent?: ValueChangeEvent
  onValueChange?: (value: string) => void
  onMentionTargetChange?: (mentionTargetId: string[]) => void
  onRelatedEntityChange?: (relatedEntities: RelatedEntity[]) => void
}

const entityTypeValues: string[] = Object.values(EntityType)
const isEntityType = (value: unknown): value is EntityType =>
  typeof value === 'string' && entityTypeValues.includes(value)

/**
 * HTML文字列から関連エンティティ情報を抽出する
 * @param htmlStr HTML文字列
 * @returns 関連エンティティの配列
 */
const getRelatedEntitiesFromHtml = (htmlStr: string): RelatedEntity[] => {
  // 例）<span data-entity-type="project" data-entity-record-id="XXXX"></span>
  const selector = `[${entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME}="${entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.VALUE}"]`
  // getAttributeSetsFromHtmlで、上記セレクタにマッチするタグのdata-entity-typeとdata-entity-record-idを抽出し、RelatedEntityの配列に整形して返す
  const relatedEntities = getAttributeSetsFromHtml(htmlStr, selector, [
    entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME,
    entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME,
  ] as const)
    .map((attributes) => {
      const entityType =
        attributes[entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME]
      const entityRecordId =
        attributes[entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME]

      if (!isEntityType(entityType) || entityRecordId.length === 0) {
        return null
      }

      return {
        entityType,
        entityRecordId,
      }
    })
    .filter((entity): entity is RelatedEntity => entity !== null)

  // 関連エンティティの配列をentityTypeとentityRecordIdの昇順でソートして返す
  return _.sortBy(relatedEntities, ['entityType', 'entityRecordId'])
}

/**
 * LexicalのonChangeイベントをハンドリングするためのプラグイン。
 * 発火タイミングをvalueChangeEventで変更できる
 */
export const OnChangeHtmlPlugin: FC<Props> = memo((props) => {
  const [editor] = useLexicalComposerContext()

  // eslintreact-hooks/exhaustive-depsの推奨に従い、useCallback内部で使う関数を分割しておく
  const {
    onValueChange: propsOnValueChange,
    onMentionTargetChange: propsOnMentionTargetChange,
    onRelatedEntityChange: propsOnRelatedEntityChange,
  } = props

  // メンション対象ユーザ
  const [mentionTargetIds, setMentionTargetIds] = useState<string[]>([])
  // 関連エンティティ
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntity[]>([])

  // 親に値変更イベントを通知する
  const raiseValueChangeEvent = useCallback(() => {
    // 値変更イベントの引数は現在入力されているRich textのHTML表現
    const contentAsHTML = $generateHtmlFromNodes(editor)
    // 値変更イベント通知
    propsOnValueChange?.(contentAsHTML)

    // contentAsHTMLを解析してメンション対象を取得し、変更があればcallbackする
    // 例）<span data-mention-id="XXXX">ユーザー1_T1</span>
    const newMentionTargetIds = getAttributesFromHtml(
      contentAsHTML,
      `${mentionConst.TAG_NAME.toLowerCase()}.${mentionConst.CLASS_NAME}`,
      mentionConst.MENTION_ID_DATA_ATTRIBUTE
    ).sort() //並び順を固定する

    if (!_.isEqual(newMentionTargetIds, mentionTargetIds)) {
      setMentionTargetIds(newMentionTargetIds)
      propsOnMentionTargetChange?.(newMentionTargetIds)
    }

    // contentAsHTMLを解析して関連エンティティを取得し、変更があればcallbackする
    const newRelatedEntities = getRelatedEntitiesFromHtml(contentAsHTML)

    if (!_.isEqual(newRelatedEntities, relatedEntities)) {
      setRelatedEntities(newRelatedEntities)
      propsOnRelatedEntityChange?.(newRelatedEntities)
    }
  }, [
    editor,
    mentionTargetIds,
    propsOnMentionTargetChange,
    propsOnRelatedEntityChange,
    propsOnValueChange,
    relatedEntities,
  ])

  useEffect(() => {
    // mergeRegisterに渡すコマンドを空配列で用意しておき、
    // valueChangeEvent設定に応じて値更新イベントの発行タイミングを追加する
    const registerCommands = []

    switch (props.valueChangeEvent) {
      case 'change': {
        // 'change'が渡された場合はBLUR_COMMANDで値更新イベントが走るようにする
        registerCommands.push(
          // blur時に走らせる処理を登録
          editor.registerCommand(
            BLUR_COMMAND,
            () => {
              // 値更新イベントを発行する
              editor.update(() => {
                raiseValueChangeEvent()
              })
              return true
            },
            COMMAND_PRIORITY_EDITOR
          )
        )
        break
      }
      case 'input': {
        // 'input'が渡された場合は単にLexicalのUpdateを検知して値更新イベントが走るようにする
        registerCommands.push(
          editor.registerUpdateListener(() => {
            editor.update(() => {
              raiseValueChangeEvent()
            })
          })
        )
        break
      }
      default:
        // 現状はCrewHtmlEditorの方でデフォルト値として'change'が渡されているため、このエラーが発生することはない
        throw new Error(
          `[OnChange] Unknown valueChangeEvent :${props.valueChangeEvent}`
        )
    }

    // 設定されたコマンドを登録する
    return mergeRegister(...registerCommands)
  }, [editor, raiseValueChangeEvent, props.valueChangeEvent])

  return null
})
